import React from 'react'
import { FaqItem } from 'components/AiPage/FaqAiPage'
import type { AiIndustrySolutionsData } from 'components/AiIndustrySolutions'
import type { AiIndustryToolsData } from 'components/AiIndustryTools'
import type { ProvenChannelData as ProvenChannelDataType } from 'components/AiInMarketingPage/ProvenChannel'
import {
  AiB2BIcon,
  AiEducationIcon,
  AiFinanceIcon,
  AiFMCGIcon,
  AiHealthcareIcon,
  AiMediaIcon,
  AiRetailIcon,
  AiTravelIcon,
} from 'components/Common/Icon'
import AiToolsIcon1, {
  AiStretagyIcon1,
  AiStretagyIcon2,
  AiStretagyIcon3,
  AiStretagyIcon5,
  AiStretagyIcon4,
  AiToolsIcon2,
  AiToolsIcon3,
  AiToolsIcon4,
  AiStretagyIcon6,
} from 'components/AiInMarketingPage/icons'

export interface WhyGrowthSectionItem {
  iconKey: string
  title: string
}

export interface WhyGrowthSectionData {
  title: string
  subtitle: React.ReactNode
  items: WhyGrowthSectionItem[]
}

export const aiIndustrySolutionsData: AiIndustrySolutionsData = {
  heading: 'AI Marketing Solutions Built for Your Industry and Customer Base',
  subheading:
    "AI marketing isn't one-size-fits-all. WebClues Infotech tailors solutions for each industry to maximize relevance and ROI",
  items: [
    {
      id: 'retail-e-commerce',
      title: 'Retail & E-Commerce',
      description:
        'Personalized offers, predictive product recommendations, and optimized pricing.',
      Icon: AiRetailIcon,
    },
    {
      id: 'b2b-saas',
      title: 'B2B & SaaS',
      description:
        'Account-based targeting, predictive lead scoring, and automated nurturing.',
      Icon: AiB2BIcon,
    },
    {
      id: 'finance-banking',
      title: 'Finance & Banking',
      description:
        'Customer retention, churn prevention, and cross-sell/up-sell optimization.',
      Icon: AiFinanceIcon,
    },
    {
      id: 'healthcare-wellness',
      title: 'Healthcare & Wellness',
      description:
        'Patient engagement, appointment reminders, and personalized health campaigns.',
      Icon: AiHealthcareIcon,
    },
    {
      id: 'travel-hospitality',
      title: 'Travel & Hospitality',
      description:
        'Dynamic pricing, personalized trip suggestions, and engagement campaigns.',
      Icon: AiTravelIcon,
    },
    {
      id: 'education-edtech',
      title: 'Education & EdTech',
      description:
        'Enrollment predictions, personalized learning recommendations, and engagement automation.',
      Icon: AiEducationIcon,
    },
    {
      id: 'fmcg-consumer-goods',
      title: 'FMCG & Consumer Goods',
      description:
        'Hyper-local campaigns, demand forecasting, and targeted promotions.',
      Icon: AiFMCGIcon,
    },
    {
      id: 'media-entertainment',
      title: 'Media & Entertainment',
      description:
        'Content personalization, audience segmentation, and engagement analytics.',
      Icon: AiMediaIcon,
    },
  ],
}

export const aiIndustryToolsData: AiIndustryToolsData = {
  heading: 'AI Marketing That Works With the Tools Your Team Already Uses',
  subheading:
    'Experience seamless inregration of AI marketing with your existing tools:',
  items: [
    {
      id: 'b2b-saas',
      title: 'CRM:',
      description: 'Salesforce, HubSpot, Microsoft Dynamics, Zoho',
      Icon: AiToolsIcon1,
    },
    {
      id: 'finance-banking',
      title: 'Email & Automation:',
      description: 'Marketo, Klaviyo, Pardot, ActiveCampaign',
      Icon: AiToolsIcon2,
    },
    {
      id: 'healthcare-wellness',
      title: 'Ad Platforms:',
      description:
        'Google Ads, Facebook Ads, LinkedIn Campaign Manager, Amazon DSP',
      Icon: AiToolsIcon3,
    },
    {
      id: 'travel-hospitality',
      title: 'Analytics & Data:',
      description: 'Google Analytics, Mixpanel, Amplitude, Snowflake, BigQuery',
      Icon: AiToolsIcon4,
    },
  ],
}

export const ProvenChannelData: ProvenChannelDataType = {
  title: 'Proven AI in Marketing Use Cases Delivering ROI Across Every Channel',
  description:
    'AI marketing drives measurable results when applied strategically across multiple touchpoints. Some high-impact use cases include:',
  challenges: [
    {
      id: 'predictive-lead-generation',
      text: (
        <>
          <span>Predictive Lead Generation:</span> AI identifies prospects with
          the highest likelihood to convert, reducing acquisition costs.
        </>
      ),
    },
    {
      id: 'dynamic-content-recommendations',
      text: (
        <>
          <span>Dynamic Content Recommendations:</span> Personalized emails, web
          content, and product suggestions improve engagement and sales.
        </>
      ),
    },
    {
      id: 'automated-ad-optimization',
      text: (
        <>
          <span>Automated Ad Optimization:</span> Machine learning adjusts bids,
          creatives, and targeting in real time to maximize ROI.
        </>
      ),
    },
    {
      id: 'customer-retention-campaigns',
      text: (
        <>
          <span>Customer Retention Campaigns:</span> AI predicts churn and
          triggers timely, relevant engagement to keep customers loyal.
        </>
      ),
    },
    {
      id: 'social-media-intelligence',
      text: (
        <>
          <span>Social Media Intelligence:</span> AI analyzes trends and
          sentiment to optimize content and timing across platforms.
        </>
      ),
    },
    {
      id: 'email-messaging-automation',
      text: (
        <>
          <span>Email & Messaging Automation:</span> Personalization at scale
          increases open rates and conversions while reducing manual effort.
        </>
      ),
    },
  ],
}

export const NextWaveData: ProvenChannelDataType = {
  title: 'The Next Wave of AI in Marketing and How to Stay Ahead of It',
  description:
    'The AI marketing landscape is evolving rapidly and staying ahead requires anticipating trends, adopting new technologies, and embedding intelligence across every customer touchpoint',
  challenges: [
    {
      id: 'gen-ai-content-creativity',
      text: (
        <>
          <span>Generative AI for Content & Creativity:</span> Automated content
          generation, ad copy, and personalized creative at scale.
        </>
      ),
    },
    {
      id: 'integration-legacy-systems',
      text: (
        <>
          <span>Integration with legacy systems:</span> Hyper-Personalized
          Customer Journeys – AI predicts intent and adapts experiences in real
          time across channels.
        </>
      ),
    },
    {
      id: 'voice-visual-search',
      text: (
        <>
          <span>Voice & Visual Search Integration:</span> Optimizing campaigns
          for emerging search behaviors beyond text.
        </>
      ),
    },
    {
      id: 'autonomous-campaign-management',
      text: (
        <>
          <span>Autonomous Campaign Management:</span> AI systems that plan,
          execute, and optimize campaigns with minimal manual intervention.
        </>
      ),
    },
    {
      id: 'cross-platform-predictive-analytics',
      text: (
        <>
          <span>Cross-Platform Predictive Analytics:</span> Real-time insights
          combining CRM, social, web, and sales data for smarter
          decision-making.
        </>
      ),
    },
    {
      id: 'ai-driven-customer-retention',
      text: (
        <>
          <span>AI-Driven Customer Retention:</span> Identifying churn risks and
          automatically triggering personalized retention strategies.
        </>
      ),
    },
    {
      id: 'ethical-transparent-ai',
      text: (
        <>
          <span>Ethical & Transparent AI Practices:</span> Building trust with
          AI models that respect privacy and provide explainable decisions.
        </>
      ),
    },
    {
      id: 'emerging-tech-integration',
      text: (
        <>
          <span>Integration with Emerging Tech:</span> IoT, AR/VR, and
          Metaverse-ready marketing strategies powered by AI.
        </>
      ),
    },
  ],
}

export const businessGainData = {
  title: (
    <>
      What Businesses Gain When They <br className="d-none d-lg-block" />{' '}
      Implement AI in Marketing
    </>
  ),
  description: 'See the tangible impact of AI marketing on business growth.',
  leftColumnItems: [
    '4M+ saved in ad spend through precise targeting',
    '70% faster campaign launches',
    '3x higher engagement with personalized content',
    '50% more qualified leads via AI scoring',
    '100% Real-time ROI tracking for smarter decisions',
  ],
} as const

export const faqData: FaqItem[] = [
  {
    question: 'What results can I expect from AI in marketing?',
    answer:
      'AI in marketing improves performance by enabling precision targeting, automated optimization, and personalized experiences. Businesses typically see better lead quality, higher engagement, improved conversion rates, and more efficient spend compared to traditional methods.',
  },
  {
    question: 'Can small businesses and startups benefit from AI marketing?',
    answer:
      'Yes. Modern AI tools scale to fit growing data sets and budgets. Even early-stage companies can use AI for audience insights, personalized messaging, automated campaigns, and basic predictive analytics to accelerate growth without heavy technical investment.',
  },
  {
    question: 'How does AI improve digital marketing?',
    answer:
      'AI enhances digital marketing by: processing large data sets faster than humans; uncovering actionable customer insights; automating repetitive tasks (like segmentation and bidding); and delivering real-time optimization across channels, from search and social to email and web.',
  },
  {
    question: 'What are common use cases for AI in marketing?',
    answer:
      'Common AI marketing applications include predictive lead scoring, behavioral segmentation, dynamic content personalization, automated ad optimization, customer churn prediction, real-time analytics, and AI-powered chatbots for engagement and lead qualification.',
  },
  {
    question: 'Which industries are using AI marketing effectively?',
    answer:
      'AI marketing is widely adopted across industries including e-commerce, SaaS and enterprise software, financial services, healthcare, travel and hospitality, retail, media and entertainment, education, consumer goods, and professional services.',
  },
  {
    question: 'How long does it take to see ROI from AI marketing?',
    answer:
      'Time to results depends on campaign complexity, data maturity, and the use cases you prioritize. Many organizations begin to see measurable improvements (better targeting, efficiency gains, lead quality lift) within 6–12 weeks post-implementation.',
  },
  {
    question: 'Do I need to replace my current marketing tools to use AI?',
    answer:
      'No. AI integrates with existing platforms like CRM systems, marketing automation, ad managers, analytics tools so you can enhance your current stack without rip-and-replace. This ensures continuity while elevating performance with AI intelligence.',
  },
  {
    question:
      'Is AI marketing compliant with data privacy laws like GDPR and CCPA?',
    answer:
      'AI can be compliant when implemented with proper governance. WebClues Infotech incorporates privacy-by-design principles, ensuring consent management, data minimization, secure storage, and transparent practices that align with regulations like GDPR and CCPA.',
  },
]

export const IntegrationData = {
  services: [
    {
      icon: AiStretagyIcon1,
      title: 'Predictive Audience Segmentation',
      description:
        'Identify micro-segments using behavior, intent, and lifecycle data.',
    },
    {
      icon: AiStretagyIcon2,
      title: 'Automated Campaign Optimization',
      description:
        'ML-driven adjustments for bids, creatives, and audience targeting.',
    },
    {
      icon: AiStretagyIcon3,
      title: 'Dynamic Content Personalization',
      description:
        'AI tailors email, web, and ad content in real time for each user.',
    },
    {
      icon: AiStretagyIcon4,
      title: 'Unified Customer Dashboard',
      description:
        'Integrates CRM, analytics, and engagement data for a 360° view.',
    },
    {
      icon: AiStretagyIcon5,
      title: 'Lead Scoring & Qualification Automation',
      description: 'Prioritize high-intent leads to accelerate conversions.',
    },
    {
      icon: AiStretagyIcon6,
      title: 'Continuous Learning & Forecasting',
      description:
        'Monitor campaigns, predict trends, and optimize strategy automatically.',
    },
  ],
}

export const AiMarketingBudgetData = {
  services: [
    {
      icon: '1',
      title: 'Wasted Ad Spend',
      description:
        'Targeting broad audiences leads to high costs and low conversions.',
    },
    {
      icon: '2',
      title: 'Delayed Insights',
      description:
        'Manual campaign analysis slows decision-making and misses trends.',
    },
    {
      icon: '3',
      title: 'Fragmented Data',
      description:
        'Scattered data across channels limits understanding of customer behavior.',
    },
    {
      icon: '4',
      title: 'Generic Messaging',
      description:
        'Without personalization, campaigns fail to engage high-intent users.',
    },
    {
      icon: '5',
      title: 'Missed Opportunities',
      description:
        'High-value prospects slip through the cracks without predictive targeting.',
    },
    {
      icon: '6',
      title: 'Inefficient Optimization',
      description:
        'Traditional methods can’t adjust campaigns in real time to maximize ROI.',
    },
  ],
}

export const AiMarketingSolutionsData = {
  services: [
    {
      icon: '1',
      title: 'Audit & Assessment:',
      description: 'Analyze current marketing stack and data readiness',
    },
    {
      icon: '2',
      title: 'Strategy Definition:',
      description:
        'Prioritize high-impact AI use cases aligned with business goals',
    },
    {
      icon: '3',
      title: 'Data Integration:',
      description: 'Combine CRM, analytics, and engagement data for insights',
    },
    {
      icon: '4',
      title: 'Model Development:',
      description:
        'Train AI models for targeting, personalization, and optimization',
    },
    {
      icon: '5',
      title: 'Testing & Launch:',
      description: 'Validate performance, run A/B tests, and deploy campaigns',
    },
    {
      icon: '6',
      title: 'Continuous Improvement:',
      description:
        'Monitor, refine, and expand AI applications for ongoing growth',
    },
  ],
}

export const FixStrategiesData = {
  title: 'How AI in Marketing Fixes What Traditional Strategies Cannot',
  description: (
    <>
      Traditional marketing struggles with delayed insights, fragmented data,
      and generic messaging. AI addresses
      <br className="d-none d-lg-block" /> these gaps by turning customer data
      into actionable strategies that drive measurable results:
    </>
  ),
}

export const DifferanceData = [
  {
    id: 1,
    parameter: 'Audience Targeting',
    inhouse: 'Broad demographics and generic segments',
    freelancing:
      'Predictive targeting using behavior, intent, and engagement signals',
  },
  {
    id: 2,
    parameter: 'Campaign Optimization',
    inhouse: 'Manual adjustments, weekly or monthly',
    freelancing: 'Real-time optimization of bids, creatives, and channels',
  },
  {
    id: 3,
    parameter: 'Content Personalization',
    inhouse: 'One-size-fits-all messaging',
    freelancing: 'Hyper-personalized content tailored to each customer journey',
  },
  {
    id: 4,
    parameter: 'Data Analysis',
    inhouse: 'Fragmented, siloed reports',
    freelancing:
      'Unified dashboards with predictive analytics and actionable insights',
  },
  {
    id: 5,
    parameter: 'Lead Management',
    inhouse: 'Manual lead scoring and routing',
    freelancing: 'AI-powered lead scoring and automated prioritization',
  },
  {
    id: 6,
    parameter: 'Decision Speed',
    inhouse: 'Slow, reactive decisions',
    freelancing: 'Instant insights and dynamic decision-making',
  },
  {
    id: 7,
    parameter: 'ROI Measurement',
    inhouse: 'Post-campaign reporting',
    freelancing:
      'Continuous performance tracking and predictive ROI forecasting',
  },
  {
    id: 8,
    parameter: 'Customer Experience',
    inhouse: 'Generic and inconsistent',
    freelancing:
      'Context-aware, personalized, and relevant at every touchpoint',
  },
]

export const whyGrowthSectionData: WhyGrowthSectionData = {
  title: 'Why Growth-Focused Teams Trust WebClues for AI Marketing Delivery',
  subtitle: (
    <>
      Partner with a growth marketing team combining AI expertise, marketing
      strategy, flexible engagement models, and measurable ROI. Dedicated
      project managers
      <br className="d-none d-lg-block" /> ensure seamless, secure, and timely
      delivery
    </>
  ),
  items: [
    { iconKey: 'enterprise', title: 'AI-First Methodology' },
    { iconKey: 'threat', title: 'Flexible Engagement Models' },
    { iconKey: 'soc', title: 'Expert Marketing & AI Team' },
    { iconKey: 'fraud', title: 'Comprehensive Tech Stack' },
    { iconKey: 'cloud', title: 'Trend-Responsive Strategies' },
    { iconKey: 'cyberphysical', title: 'Dedicated Client Success Manager' },
    { iconKey: 'integration', title: 'Secure & Transparent Deployment' },
    { iconKey: 'growth', title: 'Measurable Growth Focus' },
  ],
}
